
import { supabase } from "@/integrations/supabase/client";
import { ZelleFormValues } from "./zelleFormSchema";
import { processComponentDetails, calculateTotalPrice } from "./zelleOrderUtils";
import { toast } from "sonner";
import { CartItem } from "@/types/marketplace";

/**
 * Submit Zelle order to the database
 */
export const submitZelleOrder = async (
  data: ZelleFormValues, 
  configuration: any = null,
  cartItems: CartItem[] = []
) => {
  try {
    // Check if user is logged in
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id || null;
    const timestamp = new Date().toISOString();
    
    // Generate a unique reference code for the order
    const referenceCode = `ZELLE_${Date.now()}_${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    
    // Determine if this is a cart checkout or a PC configuration checkout
    const isCartCheckout = cartItems && cartItems.length > 0;
    
    // Calculate the total price
    let totalAmount = 0;
    
    if (isCartCheckout) {
      // For cart checkout, sum up all item prices
      const subtotal = cartItems.reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0);
      
      // Check if we have custom PCs in the cart (for assembly fee)
      const hasCustomPC = cartItems.some(item => item.product_type === 'custom');
      const assemblyFee = hasCustomPC ? 99 : 0;
      
      // Add tax - based on subtotal + assembly fee
      const tax = Math.round((subtotal + assemblyFee) * 0.08);
      
      // Free shipping
      const shipping = 0;
      
      totalAmount = subtotal + assemblyFee + tax + shipping;
    } else {
      // For PC configuration, calculate with assembly fee, etc.
      totalAmount = calculateTotalPrice(configuration, null);
    }
    
    console.log("Calculated total amount for Zelle order:", totalAmount);
    
    // Create the order record - works for both guest and authenticated users
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .insert({
        user_id: userId, // Will be null for guest checkout
        total: totalAmount,
        status: 'pending',
        created_at: timestamp,
        updated_at: timestamp,
        affiliate_code: null,
        free_shipping_applied: true, // Set free shipping flag
        shipping_discount: 0,
        coupon_code: null,
        discount_amount: 0,
      })
      .select()
      .single();

    if (orderError) {
      console.error('Error creating order:', orderError);
      
      // If the error is related to RLS policies, create a more friendly error message
      if (orderError.code === '42501') {
        throw new Error("Unable to create order. Please try signing in first.");
      }
      
      throw new Error(`Error creating order: ${orderError.message}`);
    }

    console.log('Order created:', orderData);
    
    if (isCartCheckout) {
      // Create order items for each cart item
      for (const item of cartItems) {
        const { error: itemError } = await supabase
          .from('order_items')
          .insert({
            order_id: orderData.id,
            product_name: item.product_name,
            product_id: item.product_id,
            quantity: item.quantity,
            price: item.price,
            product_details: {
              product_type: item.product_type,
              ...(item.details || {}),
              ...(item.configuration ? { configuration: item.configuration } : {})
            },
          });

        if (itemError) {
          console.error('Error creating order item:', itemError);
          throw new Error(`Error creating order item: ${itemError.message}`);
        }
      }
    } else {
      // Create order item for the PC configuration
      const productDetails = processComponentDetails(configuration);
      
      const { error: itemError } = await supabase
        .from('order_items')
        .insert({
          order_id: orderData.id,
          product_name: 'Custom Gaming PC',
          product_id: 'custom-pc',
          quantity: 1,
          price: totalAmount,
          product_details: productDetails,
        });

      if (itemError) {
        console.error('Error creating order item:', itemError);
        throw new Error(`Error creating order item: ${itemError.message}`);
      }
    }

    // Format the shipping address in a consistent way for better parsing
    const formattedShippingAddress = `${data.address.street}, ${data.address.city}, ${data.address.state} ${data.address.zip}`;
    
    // Format the customer information in a structured way for better parsing later
    const zelleNotes = `Customer: ${data.customer_name}. Shipping Address: ${formattedShippingAddress}`;

    // Create payment transaction
    const { error: paymentError } = await supabase
      .from('payment_transactions')
      .insert({
        order_id: orderData.id,
        amount: totalAmount,
        status: 'pending',
        payment_method: 'zelle',
        customer_email: data.customer_email,
        transaction_id: referenceCode,
        zelle_notes: zelleNotes,
      });

    if (paymentError) {
      console.error('Error creating payment record:', paymentError);
      throw new Error(`Error creating payment: ${paymentError.message}`);
    }

    // Send order confirmation email via Supabase Edge Function
    try {
      await supabase.functions.invoke('order-notifications', {
        body: { 
          type: "order_placed",
          email: data.customer_email,
          orderDetails: {
            orderId: orderData.id,
            orderTotal: totalAmount,
            paymentMethod: 'zelle',
            zelleEmail: "colin@battleforgepc.com",
            shippingAddress: formattedShippingAddress,
            customerName: data.customer_name
          }
        }
      });
    } catch (emailError) {
      console.error('Error sending order confirmation email:', emailError);
      // Don't throw here, just log the error as it's not critical
    }

    // Return ID with the correct parameter name for the success page
    return `order_id=${orderData.id}&paymentMethod=zelle`;
  } catch (error) {
    // Re-throw the error to be handled by the calling function
    throw error;
  }
};
